import { useRef, useState, SyntheticEvent } from 'react'

import useVideoPosterUrl from '~/hooks/useVideoPosterUrl'

import VideoPlayer from './VideoPlayer/VideoPlayer'
import { SourceType, TrackType } from './VideoPlayer/VideoPlayer.types'
import VideoPosterOverlay from './VideoPosterOverlay'

interface BootcampVideoPlayerConfig {
  src: string
  srcType?: string
  fallbackSrc?: string
  fallbackSrcType?: string
  autoplay?: boolean
  muted?: boolean
  height?: string
  width?: string
  loop?: boolean
  showControls?: boolean
  preload?: 'auto' | 'metadata' | 'none'
  allowDownload?: boolean
  videoUrls?: SourceType[]
  videoTracks?: TrackType[]
  onPlay?: (e: SyntheticEvent<HTMLVideoElement, Event>) => void
  onEnd?: () => void
  /** Poster headline; bootcamp uses the video's own description. */
  posterTitle?: string
}

const BootcampVideo = ({
  src,
  srcType,
  fallbackSrc,
  fallbackSrcType,
  autoplay = false,
  loop = false,
  showControls = true,
  muted = false,
  preload = 'auto',
  videoUrls = [],
  videoTracks = [],
  onPlay,
  onEnd,
  posterTitle = '',
}: BootcampVideoPlayerConfig): React.ReactElement => {
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false)

  const onVideoPlay = (e: SyntheticEvent<HTMLVideoElement, Event>): void => {
    setHasStartedPlaying(true)
    if (onPlay) {
      onPlay(e)
    }
  }

  const videoRef = useRef<HTMLVideoElement>(null)
  // Not tied to a curriculum level, so the background follows the user's chosen grade.
  const videoPoster = useVideoPosterUrl()
  const originalSources = videoUrls.length ? videoUrls : [{ src, type: srcType || 'video/mp4' }]
  const sources = fallbackSrc
    ? [{ src: fallbackSrc, type: fallbackSrcType ?? 'video/mp4' }, ...originalSources]
    : originalSources

  return (
    <div className="w-full relative">
      <VideoPlayer
        enableAnalytics
        onEnd={onEnd}
        onPlay={onVideoPlay}
        options={{
          autoplay,
          controls: showControls,
          loop,
          muted,
          poster: videoPoster,
          preload,
          sources,
          tracks: videoTracks,
        }}
        videoCategory="bootcamp"
        videoRef={videoRef}
      />
      {!hasStartedPlaying && <VideoPosterOverlay title={posterTitle} />}
    </div>
  )
}

export default BootcampVideo
